import { m } from 'framer-motion';
import { useState, useCallback, useEffect, useRef } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

// hooks

import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';

// components

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';

//

import NotificationItem, { WorkspaceAccessItem, BrandInfringementItem } from './notification-item';
import { Invitation, useInvitationsQuery } from 'src/context/api/workspaces';
import { Button, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetBrandsQuery } from 'src/context/api/competitorsMonitoring/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { Job, Notification, useExportJobsQuery } from 'src/context/api/data/api';
import useNotifications from './use-notifications';
import { filterByType } from './notifications.utils';
import { bellNotifications, computeTabCounts } from './notifications-popover.helpers';
import { BRAND_INFRINGEMENT_NOTIFICATIONS_ENABLED } from './brand-infringement-alerts';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

/**
 * Notifications Popover
 *
 * This component is used to display the notifications popover.
 * It is used to display the notifications, export jobs, and other information.
 *
 * @returns {JSX.Element} The Notifications Popover component
 */

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const [currentTab, setCurrentTab] = useState('all');
  const workspace: any = useCurrentWorkspace();
  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);
  const invitations = useInvitationsQuery(null);
  const { data: exportJobs, refetch: refetchExportJobs } = useExportJobsQuery(undefined, { pollingInterval: 5 * 1000 });
  const exportJobStatusesRef = useRef<Record<string | number, string>>({});
  const exportJobsInitializedRef = useRef(false);
  const downloadExport = useCallback((exportId: string | number) => {
    const link = document.createElement('a');
    link.href =
      process.env.CROSS_API_URL +
      `/traffic/export/${exportId}/download`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);
  useEffect(() => {
    if (!exportJobs) {
      return;
    }
    const jobs = exportJobs.jobs ?? [];
    if (!exportJobsInitializedRef.current) {
      jobs.forEach((job: Job) => {
        exportJobStatusesRef.current[job.id] = job.status;
      });
      exportJobsInitializedRef.current = true;
      return;
    }
    let downloadStarted = false;
    jobs.forEach((job: Job) => {
      const previousStatus =
        exportJobStatusesRef.current[job.id];
      if (
        job.status === 'completed' &&
        previousStatus !== 'completed' &&
        previousStatus !== 'downloaded'
      ) {
        downloadExport(job.id);
        const exportLabel =
          String(job.name ?? 'Traffic export')
            .replace(/^\[([^\]]+)\]\s*/, '$1 ')
            .replace(
              /Traffic details export$/i,
              'Traffic export'
            );
        enqueueSnackbar(
          `${exportLabel} is complete & downloaded.`,
          {
            autoHideDuration: 10000,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            content: (snackbarId) => (
              <Paper
                elevation={0}
                sx={{
                  width: 588,
                  maxWidth: 'calc(100vw - 32px)',
                  minHeight: 52,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '6px 12px 6px 8px',
                  bgcolor: '#FFFFFF',
                  borderRadius: '8px',
                  boxShadow:
                    '0px 8px 16px rgba(145, 158, 171, 0.20), 0px 0px 2px rgba(145, 158, 171, 0.24)',
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    bgcolor: '#EAF8F3',
                  }}
                >
                  <Iconify
                    icon="solar:check-circle-linear"
                    width={20}
                    sx={{
                      color: '#007867',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    color: '#333C47',
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '22px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {exportLabel} is complete & downloaded.
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    closeSnackbar(snackbarId)
                  }
                  sx={{
                    width: 24,
                    height: 24,
                    p: 0,
                    flexShrink: 0,
                    color: '#919EAB',
                  }}
                >
                  <Iconify
                    icon="mingcute:close-line"
                    width={16}
                  />
                </IconButton>
              </Paper>
            ),
          }
        );
        downloadStarted = true;
      }
      exportJobStatusesRef.current[job.id] = job.status;
    });
    if (downloadStarted) {
      window.setTimeout(() => {
        refetchExportJobs();
      }, 1000);
    }
  }, [downloadExport, exportJobs?.jobs, refetchExportJobs]);
  const { visibleNotifications } = useNotifications();
  // Server notifications the bell lists (onboarding and export ones are not).
  const listedNotifications = bellNotifications(visibleNotifications).filter(
    (notification) => notification.status !== 'dismissed'
  );
  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>
      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );
  // Counts must match the cards each list renders, including the non-
  // notification ones in the all tab: invitations.
  const tabCounts = computeTabCounts(visibleNotifications, {
    invitations: invitations?.data?.length ?? 0,
  });
  const TABS = [
    {
      value: 'all',
      label: t('dashboard.global.all'),
      count: tabCounts.all,
    },
    ...(BRAND_INFRINGEMENT_NOTIFICATIONS_ENABLED
      ? [
          {
            value: 'brand_infringement',
            label: 'Brand Infringement',
            count: tabCounts.brandInfringement,
          },
        ]
      : []),
  ];
  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color="default"
            >
              {tab.count}
            </Label>
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 3,
            },
          }}
        />
      ))}
    </Tabs>
  );
  const { data: brands } = useGetBrandsQuery({ bqTable: workspace?.bqTable }, { skip: !workspace?.bqTable });
  const renderNotificationCard = (notification: Notification) => {
    switch (notification.type) {
      case 'workspace_access':
        return <WorkspaceAccessItem key={notification.id} notification={notification as any} />;
      case 'brand_infringement':
        return (
          <BrandInfringementItem key={notification.id} notification={notification as any} onClose={drawer.onFalse} />
        );
      default:
        // bellNotifications only lists the types above.
        return null;
    }
  };
  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {invitations?.data?.map((invitation: Invitation) => (
          <NotificationItem key={invitation.id} notification={invitation} />
        ))}
        {listedNotifications.map((notification) => renderNotificationCard(notification))}
      </List>
    </Scrollbar>
  );
  const renderBrandInfringementList = (
    <Scrollbar>
      <List disablePadding>
        {filterByType(listedNotifications, 'brand_infringement').map((notification) => (
          <BrandInfringementItem key={notification.id} notification={notification as any} onClose={drawer.onFalse} />
        ))}
      </List>
    </Scrollbar>
  );
  const handleDrawerNotification = () => {
    drawer.onTrue();
  };
  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={handleDrawerNotification}
      >
        <Badge badgeContent={tabCounts.all} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>
      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
        </Stack>
        <Divider />
        {currentTab === 'all' && renderList}
        {currentTab === 'brand_infringement' && renderBrandInfringementList}
      </Drawer>
    </>
  );
}
